import { Field, ErrorMessage } from "formik"
import Select from "react-select"
import * as Yup from "yup"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { currencyOptions } from "../../utils/currencyOptions";
import { formatNumberWithCommas } from "../../utils/formatNumber";

const taxTypeOptions = [
  { value: "income", label: "Income Tax" },
  { value: "gst", label: "GST" },
  { value: "property", label: "Property Tax" },
]

const paymentStatusOptions = [
  // { value: "paid", label: "Paid" },
  // { value: "unpaid", label: "Unpaid" },
  // { value: "partial", label: "Partially Paid" },
  { value: "pending", label: "Pending" },
{ value: "partial", label: "Partially Paid" },
{ value: "paid", label: "Paid" },
{ value: "overdue", label: "Overdue" },
]

export const taxInitialValues = {
  taxType: "",
  taxYearPeriod: "",
  amountDue: "",
  dueDate: null, // updated for DatePicker
  penalty: "",
  interestCharges: "",
  paymentStatus: "",
  currency: "",
    name: "",
  }

export const taxValidationSchema = Yup.object().shape({
  taxType: Yup.string().required("Tax type is required"),
  taxYearPeriod: Yup.string()
  .matches(/^[1-9]\d*$/, "Only numbers allowed and must be 1 or greater")
  .required("Tax year/period is required"),
 amountDue: Yup.number()
  .transform((value, originalValue) =>
    originalValue === "" ? null : value
  )
  .nullable()
  .required("Due amount is required")
  .min(1, "Value must be 1 or greater"),

  dueDate: Yup.date().required("Due date is required"),
  penalty: Yup.number()
  .transform((value, originalValue) =>
    originalValue === "" ? null : value
  )
  .nullable()
  .required("Penalty amount is required")
  .min(1, "Value must be 1 or greater"),
interestCharges: Yup.number()
  .transform((value, originalValue) =>
    originalValue === "" ? null : value
  )
  .nullable()
  .required("Interest charges is required")
  .min(1, "Value must be 1 or greater"),

  paymentStatus: Yup.string().required("Payment status is required"),
  currency: Yup.string().required("Currency is required"),
name: Yup.string()
       .min(3, "Min length: 3 characters")
       .max(100, "Max length: 100 characters")
       .matches(
         /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
         "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
       )
       .required("Name is required"),
  });

const TaxPayableForm = ({ values, setFieldValue, errors, touched, localReadOnly }) => {
  return (
    <>
          {/* Name */}
      <div className="col-md-12 mb-4">
        <label className="form-label">
          Liability Name<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="name"
          placeholder="e.g., Tax Payable, Income Tax"
          className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
           readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="name" component="div" className="invalid-feedback" />
      </div>
   
    <div className="row">
      {/* Tax Type */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Tax Type<span className="text-danger">*</span>
        </label>
        <Select
          options={taxTypeOptions}
          value={taxTypeOptions.find(option => option.value === values.taxType)}
          onChange={option => setFieldValue("taxType", option.value)}
          className="react-select-container"
          classNamePrefix="react-select"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="taxType" component="div" className="text-danger small" />
      </div>

      {/* Tax Period */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Tax Year/Period<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="taxYearPeriod"
          placeholder="e.g., 2023 or Q1 2023"
          className={`form-control ${errors.taxYearPeriod && touched.taxYearPeriod ? "is-invalid" : ""}`}
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="taxYearPeriod" component="div" className="invalid-feedback" />
      </div>

      {/* Amount Due + Currency */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Amount Due<span className="text-danger">*</span>
        </label>
        <div className="d-flex gap-2">
           <div className="input-group">
          <Select
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === values.currency)}
            onChange={option => setFieldValue("currency", option.value)}
            className="currency-select"
            classNamePrefix="react-select"
            styles={{ container: base => ({ ...base, minWidth: 120 }) }}
            readOnly={localReadOnly}
             isDisabled={localReadOnly}
          />
          <Field
            type="text"
            inputMode="decimal"
            name="amountDue"
            className={`form-control ${errors.amountDue && touched.amountDue ? "is-invalid" : ""}`}
            style={{ flex: 2 }}
            placeholder="e.g., 1000"
           readOnly={localReadOnly}
             isDisabled={localReadOnly}
            value={values.amountDue ? formatNumberWithCommas(values.amountDue) : ""}
            onChange={(e) => {
              let unformatted = e.target.value.replace(/[^\d.]/g, "")
              const parts = unformatted.split(".")
              if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
              if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
              setFieldValue("amountDue", unformatted || "")
            }}
          />
          </div>
        </div>
        <ErrorMessage name="amountDue" component="div" className="invalid-feedback" />
        <ErrorMessage name="currency" component="div" className="invalid-feedback" />
      </div>

      {/* Due Date with React DatePicker */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Due Date<span className="text-danger">*</span>
        </label>
        <DatePicker
          selected={values.dueDate}
          onChange={(date) => setFieldValue("dueDate", date)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${errors.dueDate && touched.dueDate ? "is-invalid" : ""}`}
          placeholderText="Select a due date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
          minDate={new Date()}
        />
        <ErrorMessage name="dueDate" component="div" className="invalid-feedback" />
      </div>

      {/* Penalty */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Penalty</label>
        <Field
          type="text"
          inputMode="decimal"
          name="penalty"
          className={`form-control ${errors.penalty && touched.penalty ? "is-invalid" : ""}`}
          placeholder ="e.g., 50"
        readOnly={localReadOnly}
             isDisabled={localReadOnly}
          value={values.penalty ? formatNumberWithCommas(values.penalty) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("penalty", unformatted || "")
          }}
        />
        <ErrorMessage name="penalty" component="div" className="invalid-feedback" />
      </div>

      {/* Interest Charges */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Interest Charges</label>
        <Field
          type="text"
          inputMode="decimal"
          name="interestCharges"
          className={`form-control ${errors.interestCharges && touched.interestCharges ? "is-invalid" : ""}`}
          placeholder ="e.g., 20"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          value={values.interestCharges ? formatNumberWithCommas(values.interestCharges) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("interestCharges", unformatted || "")
          }}
        />
        <ErrorMessage name="interestCharges" component="div" className="invalid-feedback" />
      </div>

      {/* Payment Status */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Payment Status<span className="text-danger">*</span>
        </label>
        <Select
          options={paymentStatusOptions}
          value={paymentStatusOptions.find(option => option.value === values.paymentStatus)}
          onChange={option => setFieldValue("paymentStatus", option.value)}
          className="react-select-container"
          classNamePrefix="react-select"
         readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="paymentStatus" component="div" className="text-danger small" />
      </div>
    </div>
     </>
  )
}

export default TaxPayableForm
